import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import copy from 'copy-to-clipboard';

import Button from 'zignal-landscape/lib/Button';
import Col from 'zignal-landscape/lib/Col';
import Modal from 'zignal-landscape/lib/Modal';
import ModalHeader from 'zignal-landscape/lib/ModalHeader';
import ThemeProvider from 'zignal-landscape/lib/ThemeProvider';
import themes from 'themes';
import Loading from 'zignal-landscape/lib/Loading';
import themeable from 'zignal-landscape/lib/themeable';
import IFrameIcon from 'zignal-landscape/lib/Icon/IFrameIcon';
import Radio from 'zignal-landscape/lib/Radio';
import Label from 'zignal-landscape/lib/Label';
import Input from 'zignal-landscape/lib/Input';

import Widgets from './widgets';
import widgetMappings from './widgetMappings';

import * as actions from './WidgetEmbedModal.ducks';

const UnsupportedWidget = () => (<div>Unsupported widget.</div>);

const mapStateToProps = (
  (state) => ({
    embedCodeResponsePayload: state.WidgetEmbedModal.embedCodeResponsePayload,
    isEmbedCodeLoading: state.WidgetEmbedModal.isEmbedCodeLoading,
    errorMessage: state.WidgetEmbedModal.errorMessage
  })
);

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  return Object.assign({}, stateProps, dispatchProps, ownProps);
};

const WidgetEmbedModal = ({
  styles,
  isEmbedCodeLoading,
  widgetDimensions,
  embedCodeResponsePayload = {},
  generateEmbedCode,
  resetEmbedCode,
  errorMessage,
  widgetProps,
  campaignId
}) => {
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [isHtmlCopied, setIsHtmlCopied] = useState(false);
  const [themeRadioValue, handleThemeRadioChange] = useState('Light');
  const [customUrlValue, setCustomUrl] = useState('');
  const { embeddedWidgetUrl } = embedCodeResponsePayload;
  const [widgetPreviewDimensions, setWidgetPreviewDimensions] = useState({ width: 0, height: 0 });
  const widgetContainerRef = useRef();
  const embedContainerRef = useRef();
  const { widthPx, heightPx, widthGrid, heightGrid } = widgetDimensions;

  useLayoutEffect(() => {
    if (widgetContainerRef.current) {
      const previewContainerDimensions = {
        width: widgetContainerRef.current.offsetWidth,
        height: widgetContainerRef.current.offsetHeight
      };
      setWidgetPreviewDimensions({
        width: ((previewContainerDimensions.width - 50) / 3 * widthGrid),
        height: ((previewContainerDimensions.height - 35) / 2 * heightGrid)
      });
    }
  }, []);
  const {
    widgetType,
    widgetData,
    title,
    subtitle,
    apiArgs,
    additionalProps
  } = widgetProps;

  const handleCloseModal = () => {
    setIsModalOpen(false);
    resetEmbedCode();
  };

  const handleUrlInputChage = (e) => {
    setCustomUrl(e.target.value);
  };

  const generateEmbedSnippet = () => {
    const payload = { ...widgetProps, campaignId };
    generateEmbedCode(payload);
  };

  const copyEmbedSnippet = (e) => {
    e.stopPropagation();
    const embedcodeToCopy = embedContainerRef.current.textContent;
    setIsHtmlCopied(true);
    copy(embedcodeToCopy);
  };

  const showCustomUrlInput = window.Account.getUserEmail().endsWith('@zignallabs.com');
  const EmbeddedWidget =
    Widgets[widgetMappings[widgetType] ? widgetMappings[widgetType] : widgetType] || UnsupportedWidget;

  return (
    <Modal
      isOpen={isModalOpen}
      size="large"
      onBackdropClick={handleCloseModal}
      onEscapePress={handleCloseModal}
    >
      <ModalHeader onClose={handleCloseModal}>
        <Col xs={12}>
          <div className={styles.modalTitle}>
            <span className={styles.titleIconWrapper}>
              <IFrameIcon width="22" height="22" />
            </span>
            Embed
          </div>
        </Col>
      </ModalHeader>
      <Col xs={4}>
        <div className={styles.embedSettings}>
          <div className={styles.dataStaticText}>This data is static and will not update</div>
          <div className={styles.settingsRadioGroup}>
            <h3 className={styles.radioGroupLabel}>Theme</h3>
            <Radio
              id="lightRadio"
              data-cy="lightRadio"
              name="light-radio"
              onChange={() => handleThemeRadioChange('Light')}
              value="Light"
              size="small"
              checked={themeRadioValue === 'Light'}
            />
            &nbsp;
            <Label inline htmlFor="lightRadio" size="small">Light</Label>
            <Radio
              id="darkRadio"
              data-cy="darkRadio"
              name="dark-radio"
              onChange={() => handleThemeRadioChange('Dark')}
              value="Dark"
              size="small"
              checked={themeRadioValue === 'Dark'}
            />
            &nbsp;
            <Label inline htmlFor="darkRadio" size="small">Dark</Label>
          </div>

          {showCustomUrlInput &&
            <div data-cy="EmbeddableCustomUrl" className={styles.urlInputText}>
              <Input onChange={handleUrlInputChage} placeholder="Custom redirect url" />
            </div>
          }
          {
            embeddedWidgetUrl ? (
              <span>
                <span>Paste this into any HTML page:</span>
                <div className={styles.embedCodeContainer}>
                  <div
                    id="widget-embed-iframe-snippet"
                    className={styles.embedCodeText}
                    data-cy="widget-embed-iframe-snippet"
                    ref={embedContainerRef}
                  >
                    &lt;iframe width="{widthPx}" height="{heightPx}"
                    src="{embeddedWidgetUrl}&theme={themeRadioValue}
                    {customUrlValue ? `&customUrl=${customUrlValue}"` : '"'} frameborder="0"&gt;&lt;/iframe&gt;
                  </div>
                </div>
                <div className={styles.copyToHtmlBtn} data-cy="CopyHtmlContainer">
                  <Button
                    buttonStyle={isHtmlCopied ? 'positive' : 'secondary'}
                    onClick={copyEmbedSnippet}
                    disabled={isEmbedCodeLoading}
                  >
                    {isHtmlCopied ? 'HTML Copied' : 'Copy HTML'}
                  </Button>
                </div>
              </span>
            ) : (
              <span>
                <Button onClick={generateEmbedSnippet} disabled={isEmbedCodeLoading}>
                  Generate Embed Snippet
                </Button>
                {isEmbedCodeLoading && (<div className={styles.embedCodeLoading}><Loading /></div>)}
                {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
              </span>
            )
          }
        </div>
      </Col>

      <Col xs={8}>
        <header className={styles.previewHeader}>
          <h4 className={styles.previewHeaderTitle}>Preview</h4>
        </header>
        <div className={styles.widgetPreviewWrapper} ref={widgetContainerRef}>
          <div
            style={{ width: widgetPreviewDimensions.width, height: widgetPreviewDimensions.height }}
            className={styles.widgetPreviewContainer}
          >
            <ThemeProvider theme={themes[themeRadioValue]}>
              <EmbeddedWidget
                isEmbedded
                apiArgs={apiArgs}
                id={widgetType}
                title={title}
                subtitle={subtitle}
                data={widgetData}
                customUrl={customUrlValue}
                {...additionalProps}
              />
            </ThemeProvider>
          </div>
        </div>
      </Col>
    </Modal>
  );
};

WidgetEmbedModal.propTypes = {
  styles: PropTypes.object,
  generateEmbedCode: PropTypes.func,
  embedCodeResponsePayload: PropTypes.object,
  resetEmbedCode: PropTypes.func,
  widgetProps: PropTypes.object,
  campaignId: PropTypes.string,
  isLoading: PropTypes.bool,
  widgetDimensions: PropTypes.object,
  isEmbedCodeLoading: PropTypes.bool,
  errorMessage: PropTypes.string
};

export default connect(mapStateToProps, actions, mergeProps)(themeable(WidgetEmbedModal));

export { WidgetEmbedModal as PureWidgetEmbedModal };
